import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { styles as modalBtnsStyles } from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns.styles'
import {
  heading,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import {
  selectAvailablePlansByProduct,
  selectBillingEstimateForPlanChange,
  selectCurrentPlanByProduct,
  selectIsTrial,
} from 'ducks/billing/selectors'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_OVERVIEW } from 'ducks/drawers/types'
import { PLAN_DOWNGRADABLE_DIFF } from 'constants/billing'
import { diff } from 'util/date'
import { capitalize } from 'util/strings'
import { doFetchBillingData } from 'ducks/billing'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { INBOX, KB } from 'ducks/billing/productTypes'
import { AdminAccessDrawer } from '../../NoAccess'
import Features from '../Features'
import { styles } from '../styles'
import ContactCard from '../ContactCard'

const BillingDownGradePlan = ({
  open,
  onExit,
  onClose,
  drawerResourceId,
  drawerProductType,
  drawerBillingCycle,
}) => {
  const dispatch = useDispatch()
  const billingPlans = useSelector(state =>
    selectAvailablePlansByProduct(state, drawerProductType)
  )
  const newPlan = billingPlans.find(plan => plan.planId === drawerResourceId)

  const otherProductPlan = useSelector(state =>
    selectCurrentPlanByProduct(state, drawerProductType === INBOX ? KB : INBOX)
  )
  const {
    plan: { planId: otherProductPlanId, product: otherProductProductType } = {},
  } =
    otherProductPlan || {}

  const { nextBillDate } = useSelector(selectBillingEstimateForPlanChange)
  const isTrial = useSelector(state => selectIsTrial(state, 'inbox'))
  const overdue =
    isTrial ||
    diff('milliseconds', new Date(), nextBillDate * 1000) <
      PLAN_DOWNGRADABLE_DIFF

  const {
    drawerId: openDowngradePlanOverviewDrawerId,
    openDrawer: openDowngradePlanOverview,
  } = useDrawer({
    type: DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_OVERVIEW,
  })

  const handleClose = useCallback(
    () => {
      onClose({
        ignoreStack: true,
      })
    },
    [onClose]
  )

  const handleContinue = useCallback(
    () => {
      onExit()
      openDowngradePlanOverview(drawerResourceId, {
        query: {
          ...buildDrawerQueryParam(
            openDowngradePlanOverviewDrawerId,
            'drawerProductType',
            drawerProductType
          ),
          ...buildDrawerQueryParam(
            openDowngradePlanOverviewDrawerId,
            'drawerLimitsConfirmed',
            true
          ),
          ...buildDrawerQueryParam(
            openDowngradePlanOverviewDrawerId,
            'drawerBillingCycle',
            drawerBillingCycle
          ),
        },
      })
    },
    [
      drawerResourceId,
      onExit,
      openDowngradePlanOverview,
      openDowngradePlanOverviewDrawerId,
      drawerProductType,
      drawerBillingCycle,
    ]
  )

  useEffect(
    () => {
      dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  useEffect(
    () => {
      if (!drawerResourceId || !newPlan) {
        onExit()
      }
    },
    [drawerResourceId, onExit, newPlan]
  )

  const footer = (
    <div css={[modalBtnsStyles.base, styles.btns]}>
      <Button type="tertiary" size="big" onClick={handleClose}>
        {overdue ? 'Close' : 'No thanks, I’d like to keep my current plan'}
      </Button>
      {!overdue && (
        <Button
          type="info"
          size="big"
          onClick={handleContinue}
          disabled={!nextBillDate}
        >
          Continue to confirm
        </Button>
      )}
    </div>
  )

  const featuresTitle = useMemo(
    () => {
      if (!overdue) {
        return (
          <>
            With{' '}
            <strong>
              {capitalize(drawerProductType)}: {newPlan?.name}
            </strong>, you’ll need to remove or disable the following features
            prior to the scheduled downgrade:
          </>
        )
      }
      return (
        <>
          To downgrade to{' '}
          <strong>
            {capitalize(drawerProductType)}: {newPlan?.name}
          </strong>, you’ll need to remove or disable the following:
        </>
      )
    },
    [overdue, newPlan?.name, drawerProductType]
  )

  if (!newPlan) {
    return null
  }

  return (
    <AdminAccessDrawer
      title="Downgrade plan"
      open={open}
      footer={footer}
      onClose={onExit}
      isLoading={!nextBillDate}
      contentStyles={styles.wrapper}
    >
      <div
        className="grui pt-12"
        css={[text.styles.textNormal, styles.container]}
      >
        <h4 className="grui" css={heading.styles.h4}>
          We’re sorry you want to downgrade
        </h4>
        <div>
          <p>
            You currently making more use of Groove than the{' '}
            <strong>{newPlan.name}</strong> plan allows.
          </p>
          <p className="grui my-12">{featuresTitle}</p>
          <Features planId={drawerResourceId} productType={drawerProductType} />
          {!!otherProductPlanId && (
            <Features
              planId={otherProductPlanId}
              productType={otherProductProductType}
            />
          )}
        </div>
      </div>
      <ContactCard />
    </AdminAccessDrawer>
  )
}

export default BillingDownGradePlan
